import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import VoiceConversation, { canConverse } from './VoiceConversation';

const CATEGORY_EMOJI = {
  exercise: '🏃',
  nutrition: '🍽️',
  sleep: '😴',
  biometric: '📏',
  mental_health: '🧠',
  therapy: '🌿',
  medical: '🩺',
  environmental: '🌤️',
  social: '👥',
  other: '•',
};

const GREETING = {
  role: 'assistant',
  text: "What have you been up to? Tell me what you ate, how you moved, or how you slept — I'll log it for you.",
};

const entryLabel = (e) => {
  const emoji = CATEGORY_EMOJI[(e.category_slug || '').split('.')[0]] || '•';
  const name = (e.entry_type || '').replace(/_/g, ' ');
  let detail = '';
  if (e.value != null) detail = ` ${e.value}${e.value_unit ? ' ' + e.value_unit : ''}`;
  else if (e.calories != null) detail = ` ${Math.round(e.calories)} cal`;
  else if (e.duration_minutes != null) detail = ` ${e.duration_minutes} min`;
  return `${emoji} ${name}${detail}`;
};

// Prefer a container the transcription API takes as-is. Safari/iOS only offers mp4;
// leaving the type empty lets that browser pick its own default rather than failing.
const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

const formatElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// Prod's nginx rejects bodies over 1MB and raising it needs hands-on sudo. Capping the
// recording bounds the upload without depending on the encoder honoring a bitrate.
// Measured: Chrome defaults to 128kbps opus (~14.5KB/s), so 60s lands near 870KB —
// under the cap with room to spare. Don't raise this without re-measuring.
const MAX_RECORDING_SECONDS = 60;
// Belt and braces: if an encoder is fatter than measured, say so plainly rather than
// letting the request bounce off the proxy as opaque HTML.
const MAX_UPLOAD_BYTES = 1000 * 1024;

const ConversationLogger = ({ onEntrySaved, defaultDate }) => {
  const [messages, setMessages] = useState([GREETING]);
  const [text, setText] = useState('');
  const [date, setDate] = useState(defaultDate || new Date().toISOString().split('T')[0]);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // The hands-free call surface (Phase E) — a separate interaction style from this typed/
  // dictated composer, entered deliberately rather than mixed in.
  const [converse, setConverse] = useState(false);
  // Shown only when a recording ends without producing text, so a silent failure on a
  // phone (where there's no console to check) still says what actually happened.
  const [diag, setDiag] = useState('');
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);
  // Lets the elapsed-timer effect trigger the auto-stop without depending on a
  // callback defined further down.
  const stopRecordingRef = useRef(null);

  // Recording needs MediaRecorder + a secure context (HTTPS or localhost). On the LAN
  // HTTP dev box getUserMedia is absent, so the mic button correctly hides itself there.
  const canRecord = typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const releaseMic = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Never leave the mic hot if the user navigates away mid-recording.
  useEffect(() => () => {
    cancelledRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (e) { /* already stopped */ }
    }
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  // Elapsed counter — the honest "still recording" signal that the old pulsing
  // button couldn't give, since a visibly climbing timer can't be faked by a pause.
  useEffect(() => {
    if (!recording) return undefined;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => {
      const next = s + 1;
      if (next >= MAX_RECORDING_SECONDS) {
        // Stop ourselves rather than let the upload bounce off the proxy.
        toast('That’s a long one — wrapping it up so it sends cleanly.');
        stopRecordingRef.current?.();
      }
      return next;
    }), 1000);
    return () => clearInterval(id);
  }, [recording]);

  const sendForTranscription = useCallback(async (blob, mimeType) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('ogg') ? 'ogg' : 'webm';
      form.append('audio', blob, `clip.${ext}`);
      // apiClient defaults every request to Content-Type: application/json. For a
      // FormData upload that header must be removed so the browser can set
      // multipart/form-data WITH its boundary — otherwise the server sees a JSON
      // content-type on a multipart body, can't find the field, and returns 422.
      const res = await apiClient.post('/api/health/transcribe', form, {
        timeout: 120000,
        headers: { 'Content-Type': undefined },
      });
      const transcript = (res.data?.text || '').trim();
      if (!transcript) {
        // Audio arrived but held no speech. Note the size so "sent nothing" and
        // "sent audio nobody could hear" stay distinguishable.
        setDiag(`Sent ${Math.round(blob.size / 1024)}KB but no words were recognized.`);
        toast('I didn’t catch any words there — try again a bit closer to the mic.');
        return;
      }
      // Append rather than replace, so dictating twice builds one message and never
      // silently eats what the user already typed.
      setText((prev) => (prev.trim() ? `${prev.trim()} ${transcript}` : transcript));
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      setDiag(`Upload failed (${status || 'no response'}) after ${Math.round(blob.size / 1024)}KB.`);
      if (status === 429) {
        toast.error(detail || 'Slow down a moment — too many recordings.');
      } else if (status === 413) {
        // Can come from nginx as HTML rather than our JSON, so don't trust `detail`.
        toast.error('That recording was too long. Try a shorter one, or type it.');
      } else {
        toast.error(detail || 'Couldn’t transcribe that. Please try again or type it.');
      }
    } finally {
      setTranscribing(false);
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!canRecord || recording || transcribing) return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      if (e.name === 'NotAllowedError' || e.name === 'SecurityError') {
        toast.error('Microphone access denied. Please allow microphone permissions.');
      } else {
        toast.error('Couldn’t reach your microphone. You can type instead.');
      }
      return;
    }

    cancelledRef.current = false;
    streamRef.current = stream;
    chunksRef.current = [];
    setDiag('');

    const mimeType = pickMimeType();
    let recorder;
    try {
      // Deliberately no audioBitsPerSecond: forcing a bitrate is encoder-specific and
      // a mobile encoder that dislikes the value can run happily while producing no
      // data at all. The 1MB proxy cap is enforced by the duration limit below
      // instead, which doesn't depend on the encoder honoring anything.
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      releaseMic();
      toast.error('Recording isn’t supported in this browser. Please type instead.');
      return;
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onerror = () => {
      releaseMic();
      setRecording(false);
      toast.error('Recording stopped unexpectedly. Please try again.');
    };

    recorder.onstop = () => {
      releaseMic();
      setRecording(false);
      const type = recorder.mimeType || mimeType || 'audio/webm';
      const chunkCount = chunksRef.current.length;
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      // Unmount or an explicit discard — don't spend a transcription on it. This used
      // to return silently, which made a failed recording indistinguishable from a
      // broken one; now every path that ends without text says why.
      if (cancelledRef.current) {
        setDiag(`Recording was discarded before it could be sent (${chunkCount} chunks).`);
        return;
      }
      if (blob.size < 1024) {
        // The mic ran but produced no audio — an encoder/permission problem, not the
        // user talking too briefly. Report what we actually got so it's diagnosable.
        setDiag(`No audio captured — ${chunkCount} chunks, ${blob.size} bytes, ${type}.`);
        toast.error('That recording came through empty. Please try again, or type it.');
        return;
      }
      if (blob.size > MAX_UPLOAD_BYTES) {
        setDiag(`That clip is ${Math.round(blob.size / 1024)}KB — too big to upload. Try a shorter one.`);
        toast.error('That recording was too long to send. Please try a shorter one.');
        return;
      }
      setDiag('');
      sendForTranscription(blob, type);
    };

    recorderRef.current = recorder;
    // Timeslice: force a data callback every second instead of relying solely on the
    // final flush at stop(). Mobile encoders are less reliable about that last flush,
    // and this way a recording that starts producing audio can't be lost wholesale.
    recorder.start(1000);
    setRecording(true);
  }, [canRecord, recording, transcribing, releaseMic, sendForTranscription]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      // Flush whatever the encoder is holding before stopping, so the final second
      // of speech can't be dropped along with the last partial chunk.
      try { recorderRef.current.requestData(); } catch (e) { /* not all engines allow it */ }
      recorderRef.current.stop();
    }
  }, []);
  stopRecordingRef.current = stopRecording;

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    if (recording) stopRecording();

    // History = the conversation so far (skip the seeded greeting), last 12 turns.
    const history = messages
      .filter((m) => m !== GREETING)
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.text }));

    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setText('');
    setSending(true);

    try {
      const res = await apiClient.post('/api/health/conversation', {
        message: trimmed,
        history,
        date,
      }, { timeout: 180000 });
      const { reply, entries, logged_count } = res.data;
      setMessages((prev) => [...prev, { role: 'assistant', text: reply, entries: entries || [] }]);
      if (logged_count > 0 && onEntrySaved) onEntrySaved();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 429) {
        toast.error(detail || 'Slow down a moment — too many messages.');
      } else if (error.response?.status === 401) {
        toast.error('Please log in again to keep logging.');
      } else {
        toast.error(detail || 'Something went wrong. Please try again.');
      }
      // Surface the failure inline so the chat doesn't look stuck.
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: "Sorry — I couldn't log that just now. Mind trying again?",
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card-glow rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-primary-500/8 via-primary-400/4 to-transparent px-6 py-4 border-b border-surface-border dark:border-surface-dark-border">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Log with a message</h2>
        <div className="flex flex-wrap items-center gap-3">
          {canConverse && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConverse(true)}
              className="h-9 text-sm gap-1.5"
              title="Talk hands-free — no tapping between turns"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M12 1.5a3 3 0 0 0-3 3v6a3 3 0 1 0 6 0v-6a3 3 0 0 0-3-3Z" />
                <path d="M6.75 10.5a.75.75 0 0 0-1.5 0 6.75 6.75 0 0 0 6 6.709V19.5h-2.25a.75.75 0 0 0 0 1.5h6a.75.75 0 0 0 0-1.5H12.75v-2.291a6.75 6.75 0 0 0 6-6.709.75.75 0 0 0-1.5 0 5.25 5.25 0 0 1-10.5 0Z" />
              </svg>
              Have a conversation
            </Button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Date</span>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40 h-9 text-sm"
            />
          </div>
        </div>
      </div>

      {converse && (
        <VoiceConversation
          onClose={() => setConverse(false)}
          onEntrySaved={onEntrySaved}
          defaultDate={date}
        />
      )}

      {/* Conversation */}
      <div ref={scrollRef} className="px-6 py-5 space-y-4 max-h-[28rem] overflow-y-auto">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-primary-500 text-[#1a1200] rounded-br-sm'
                  : 'bg-gray-100 dark:bg-surface-dark-raised text-gray-900 dark:text-gray-100 rounded-bl-sm'
              }`}
            >
              {m.text}
              {m.entries && m.entries.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.entries.map((e, j) => (
                    <span
                      key={j}
                      className="inline-flex items-center rounded-full bg-white/70 dark:bg-gray-900/50 border border-primary-200 dark:border-primary-800 px-2 py-0.5 text-xs font-medium text-primary-800 dark:text-primary-200"
                    >
                      {entryLabel(e)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-surface-dark-raised rounded-2xl rounded-bl-sm px-4 py-3">
              <span className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-surface-border dark:border-surface-dark-border p-4">
        <div className="flex items-stretch gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder='e.g. "a loaf of sourdough, avocado, and some tater tots for dinner"'
            maxLength={1000}
            rows={2}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className="flex-1 resize-none"
          />
          <div className="flex flex-col gap-2 shrink-0">
            {canRecord && (
              <Button
                type="button"
                variant={recording ? 'destructive' : 'outline'}
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                title={recording ? 'Stop recording' : 'Record a voice note'}
                className={`w-12 flex-1 ${recording ? 'ring-2 ring-red-500 bg-red-600 hover:bg-red-700' : ''}`}
              >
                {recording ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 0 1 7.5 5.25h9a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-9Z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
                  </svg>
                )}
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSend}
              disabled={sending || !text.trim()}
              className="w-12 flex-1"
              title="Send"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
              </svg>
            </Button>
          </div>
        </div>
        {recording ? (
          // The climbing timer is the proof it's still going. Nothing restarts the
          // recording now, so there's no platform chime to explain away.
          <span className="mt-1 flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
            </span>
            Recording {formatElapsed(elapsed)} — take your time, then tap the square.
          </span>
        ) : transcribing ? (
          <span className="mt-1 block text-sm font-medium text-primary-700 dark:text-primary-300">
            Writing down what you said…
          </span>
        ) : diag ? (
          <span className="mt-1 block text-sm font-medium text-red-600 dark:text-red-400">
            {diag}
          </span>
        ) : (
          <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
            {text.length}/1000 · Enter to send, Shift+Enter for a new line
          </span>
        )}
      </div>
    </div>
  );
};

export default ConversationLogger;
